import type { PageFormat } from "./format";

/**
 * Which editor sits behind `PageEditor`:
 * - `astromark`: `@orbitalwatch/astromark-editor` (WYSIWYG / split / source, AstroMark preview);
 * - `legacy`: `react-markdown-editor-lite`, the editor Ark has always used, with `renderPage` as its preview;
 * - `auto` (default): `astromark` for AstroMark pages, `legacy` for markdown-it pages. The AstroMark editor previews (and,
 *   in WYSIWYG, re-serialises) a body as AstroMark, which would rewrite a markdown-it page; so markdown-it pages keep the
 *   legacy editor until they are migrated.
 */
export type PageEditorKind = "auto" | "astromark" | "legacy";

/** Module default for `editor`: flip to `legacy` to take the AstroMark editor out everywhere without touching screens. */
export const DEFAULT_PAGE_EDITOR: PageEditorKind = "auto";

function editorFromSearch(search: string | undefined): "astromark" | "legacy" | undefined {
    if (!search) return undefined;
    try {
        const value = new URLSearchParams(search).get("editor");
        return value === "astromark" || value === "legacy" ? value : undefined;
    } catch {
        return undefined;
    }
}

/** The editor for a page: an explicit `astromark`/`legacy` wins, then the dev `?editor=` override, then by format. */
export function resolvePageEditor(
    format: PageFormat,
    kind: PageEditorKind = DEFAULT_PAGE_EDITOR,
    search: string | undefined = typeof window !== "undefined" ? window.location.search : undefined,
    allowOverride: boolean = import.meta.env.DEV
): "astromark" | "legacy" {
    if (kind !== "auto") return kind;
    const override = allowOverride ? editorFromSearch(search) : undefined;
    if (override) return override;
    return format === "astromark" ? "astromark" : "legacy";
}
